import React from "react";
import { 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle, 
  FiFileText, 
  FiShield, 
  FiAward, 
  FiArrowRight, 
  FiUser, 
  FiSend,
  FiMoreVertical,
  FiCornerDownRight
} from "react-icons/fi";
import type { RcaRecordDto } from "../../../types/rca.types.js";

export type RcaOverviewPanelProps = {
  record: RcaRecordDto;
  onNavigateToWorkspace: () => void;
  onSubmitForReview: () => void;
  onOpenHoldModal?: (() => void) | undefined;
  onOpenCancelModal?: (() => void) | undefined;
  onOpenReopenModal?: (() => void) | undefined;
};

export function RcaOverviewPanel({
  record,
  onNavigateToWorkspace,
  onSubmitForReview,
  onOpenHoldModal,
  onOpenCancelModal,
  onOpenReopenModal,
}: RcaOverviewPanelProps) {
  const isComplete = record.statusCode === "RCA_COMPLETED";
  const isReview = record.statusCode === "RCA_UNDER_REVIEW";

  return (
    <div className="space-y-5">
      {/* Top Banner / Status Strip */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900">
                {record.rcaCode}
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">
                {record.statusName || record.statusCode || "IN PROGRESS"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-3">
              <span>Complaint: <strong className="text-slate-800">{record.complaintCode || "CMP-2026-0001"}</strong></span>
              <span>•</span>
              <span>Method: <strong className="text-slate-800">{record.rcaMethodName || "5-Why Analysis"}</strong></span>
              <span>•</span>
              <span>Owner: <strong className="text-slate-800">{record.ownerName}</strong></span>
              <span>•</span>
              <span>Priority: <span className="font-bold text-rose-600">{record.priorityLevel || "HIGH"}</span></span>
              <span>•</span>
              <span>Due: <strong className="text-slate-800">{record.targetDate ? new Date(record.targetDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "26-Aug-2026"}</strong></span>
            </div>
          </div>
        </div>

        {/* Top Header Actions */}
        <div className="flex items-center gap-2">
          {onOpenHoldModal && (
            <button
              type="button"
              onClick={onOpenHoldModal}
              className="px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 rounded-lg border border-slate-300"
            >
              Hold
            </button>
          )}

          <button
            type="button"
            onClick={onSubmitForReview}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center gap-1.5 transition-all"
          >
            <FiSend className="w-3.5 h-3.5" />
            Submit for Review
          </button>
        </div>
      </div>

      {/* Main Grid: Problem & Root Cause + Summary Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Left 2 Cols: Problem Statement & Root Cause Details */}
        <div className="md:col-span-2 space-y-4">
          {/* Problem Statement Card */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-2">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Problem Statement
            </h3>
            <p className="text-sm font-semibold text-slate-900 leading-relaxed">
              {record.problemStatement}
            </p>
            {record.problemDescription && (
              <p className="text-xs text-slate-600 pt-1 leading-normal border-t border-slate-100">
                {record.problemDescription}
              </p>
            )}
          </div>

          {/* Root Cause Card */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                <FiAward className="w-4 h-4 text-rose-600" />
                Root Cause Findings
              </h3>
              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                record.rootCause || record.rootCauseVerified 
                  ? "bg-rose-50 text-rose-700 border-rose-200" 
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}>
                {record.rootCause ? "Verified Root Cause" : "Not yet verified"}
              </span>
            </div>

            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Category: <strong className="text-slate-900">{record.rootCauseCategoryCode || "MACHINE"}</strong></span>
                <span className="text-slate-500">Confidence: <strong className="text-blue-600">{record.confidenceScore || 85}%</strong></span>
              </div>
              <p className="text-xs font-medium text-slate-800">
                {record.rootCause || "CNC machine calibration drift due to thermal expansion exceeding compensation envelope during night shifts."}
              </p>
            </div>
          </div>

          {/* Actions Preview Box */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Planned / In-Progress Actions
              </h3>
              <span className="text-xs font-semibold text-blue-600">2 Actions Linked</span>
            </div>

            <div className="space-y-2">
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
                <div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 mr-2">CORRECTIVE</span>
                  <span className="font-semibold text-slate-900">Recalibrate CNC machine Z-axis tool offset</span>
                </div>
                <div className="text-slate-500 text-[11px]">Due: 08/28 · J.Smith</div>
              </div>

              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
                <div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-800 mr-2">PREVENTIVE</span>
                  <span className="font-semibold text-slate-900">Implement automated ERP PM maintenance alerts</span>
                </div>
                <div className="text-slate-500 text-[11px]">Due: 09/05 · Kumar</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right 1 Col: Lifecycle Progress Stepper & Metric Cards */}
        <div className="space-y-4">
          {/* Progress Lifecycle Stepper Card */}
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Investigation Lifecycle
            </h3>

            <div className="space-y-3 relative pl-4 border-l-2 border-slate-200 ml-2">
              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-emerald-600 border-2 border-white"></span>
                <div className="text-xs font-bold text-slate-900">Problem Defined</div>
                <div className="text-[10px] text-slate-400">Problem & boundary fixed</div>
              </div>

              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-emerald-600 border-2 border-white"></span>
                <div className="text-xs font-bold text-slate-900">Data Collection</div>
                <div className="text-[10px] text-slate-400">3 evidence files attached</div>
              </div>

              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-blue-600 ring-4 ring-blue-100 border-2 border-white"></span>
                <div className="text-xs font-bold text-blue-600">Analysis In Progress</div>
                <div className="text-[10px] text-slate-400">5-Why chain actively edited</div>
              </div>

              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-slate-300 border-2 border-white"></span>
                <div className="text-xs font-medium text-slate-500">Root Cause Verification</div>
                <div className="text-[10px] text-slate-400">Pending QA sign-off</div>
              </div>

              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-slate-300 border-2 border-white"></span>
                <div className="text-xs font-medium text-slate-500">Actions & CAPA</div>
                <div className="text-[10px] text-slate-400">Action handoff</div>
              </div>

              <div className="relative">
                <span className="absolute -left-[23px] top-0.5 w-3.5 h-3.5 rounded-full bg-slate-300 border-2 border-white"></span>
                <div className="text-xs font-medium text-slate-500">Closure</div>
                <div className="text-[10px] text-slate-400">Finalized</div>
              </div>
            </div>

            <button
              type="button"
              onClick={onNavigateToWorkspace}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center justify-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              Continue to Workspace <FiArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Metric Badges */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm text-center">
              <span className="text-[11px] text-slate-400 block font-semibold">Evidence</span>
              <span className="text-base font-bold text-slate-900 mt-0.5 block">3 Files</span>
            </div>

            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm text-center">
              <span className="text-[11px] text-slate-400 block font-semibold">Team Size</span>
              <span className="text-base font-bold text-slate-900 mt-0.5 block">4 Members</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
